define([
	"dojo/_base/declare",
	"dijit/_WidgetBase",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/view/_query/LevelFilter",
	"csi/widget/Tooltip",
], function(declare, _WidgetBase, _, html, locale, QueryLevelFilter, Tooltip) {

	const HTML_NAME = '<div>${0}</div>';

	/*
	export type CustomViewSummaryItem = {
		id: string;
		itemDetails?: PreferenceView;
		name: string;
		urlNode: cnl;
		favorite: boolean;
		personal: boolean;
		personalUrls: Record<cnl, cnl>;
		systemWide: boolean;
		viewRoles: string[];
		getName: () => string;
		getIcon: () => string;
		setFavorite: (domNode: HTMLElement) => void;
		getItemDetails: (cnl: cnl) => Promise<PreferenceView> | null;
		isDefaultView: () => false;
		isCurrentUser: () => boolean;
		isShared: () => boolean;
		isSystemWide: () => boolean;
	}
	*/

	return declare(_WidgetBase, {
		"baseClass": "csi-preference-summary",

		/** @property {number} limitHeight - defaults to fit within the custom view drop-down (csi/view/customview/SelectPlugin) */
		limitHeight: 320,

		/** @property {CustomViewSummaryItem} item */
		item: null,

		/** @property {PreferenceView} itemDetails */
		itemDetails: null,

		/** @property {View} view */
		view: null,

		/** @property {Tooltip} tooltip */
		tooltip: null,

		postCreate: function() {
			html.setVisibility(this.domNode, false);
			this._renderName();
			this._renderDescription();
			this._renderFilters();
			this._renderGroups();
			this._renderSortBy();
		},

		startup: function() {
			this.inherited(arguments);
			this.resize();
			html.setVisibility(this.domNode, true);
		},

		destroy: function() {
			this.inherited(arguments);
			if (this.tooltip) {
				this.tooltip.destroy();
			}
		},

		/**
		 * Return true if more than one DOM node
		 */
		hasContent: function() {
			return this.domNode.children.length > 1;
		},

		/**
		 * Restructure the summary if filter conditions are overflowing
		 */
		resize: function() {
			if (!this.limitHeight) {
				return;
			}
			var summaryNode = this.domNode;
			var limitHeight = this.limitHeight;
			var summaryHeight = summaryNode.offsetHeight;

			if (summaryHeight > limitHeight) {
				// create more filter conditions node and  tooltip for overflow filter conditions
				var moreNode = html.create("div", {
					id: this.baseClass + "-more-filters",
					innerText: locale.resource("ViewPreferences_MoreFilterConditions")
				}, summaryNode.lastChild, "before");

				var jFilterNode = $("#" + summaryNode.id + " .csi-preference-summary-filters-container");
				var jNodes = jFilterNode.children();
				var jNodesCount = jNodes.length;
				var overflowNode = html.create("div", {"class": "csi-preference-summary csi-preference-summary-overflow"});
				var conditionsCount = 0;
				var i = 1;

				// remove overflow conditions
				while ((summaryNode.offsetHeight > limitHeight) && (i <= jNodesCount)) {
					var rNode = jNodes[jNodesCount - i];
					if (rNode) {
						if (rNode.tagName === "DIV") {
							conditionsCount++;
						}
						overflowNode.prepend(rNode);
					}

					i++;
				}

				// update more filter node text
				jFilterNode[0].appendChild(moreNode);
				moreNode.innerText = locale.resource("ViewPreferences_MoreFilterConditions", [conditionsCount]);
				this.tooltip = new Tooltip({
					connectId: [moreNode.id],
					position: ["after-centered"],
					label: overflowNode
				});
			}
		},

		/**
		 * Render view's name
		 */
		_renderName: function() {
			const {itemDetails} = this;
			html.create("div", {
				"class": this.baseClass + "-name bold",
				innerText: itemDetails.name
			}, this.domNode);
		},

		/**
		 * Render view's description
		 */
		_renderDescription: function() {
			const text = this.itemDetails.values.prefDescription;
			if (text) {
				html.create("div", {
					innerText: text,
					title: text,
					"class": this.baseClass + "-description"
				}, this.domNode);
			}
		},

		/**
		 * Render filters header and view's filter conditions
		 */
		_renderFilters() {
			const {domNode, item, itemDetails, view} = this;
			if (item.isDefaultView()) {
				return;
			}
			const {filterSettings} = itemDetails.values;
			if (!filterSettings) {
				return;
			}

			if (filterSettings && view.bSupportsQuerySettings) {
				const levelStore = view.getConfigurableQueryLevelStore();
				if (!levelStore) {
					return;
				}

				const ctx = view.getQueryRelativeContext();
				if (ctx) {
					let err;

					// show error if relative values cannot be found
					levelStore.query().forEach((ql) => {
						const filter = filterSettings[ql.id];
						if (filter) {
							const qlf = new QueryLevelFilter(view, filter, ql);

							if (qlf.hasRelativeFilters() && !qlf.getRelativeValues(ctx)) {
								err = locale.resource("PreferenceFilterRelativeValueError");
							}
						}
					});

					if (err) {
						html.create("div", {
							"class": "error",
							innerText: err
						}, domNode);

						return;
					}
				}

				levelStore.query().forEach((ql) => {
					var qlf = new QueryLevelFilter(view, filterSettings[ql.id], ql);
					var widgets = qlf.getFilterConditions();
					if (widgets.length) {
						var filterContainer = html.create("div", {"class": this.baseClass + "-filters-container"}, domNode);
						var loc = qlf.getLocale();

						html.create("label", {
							innerText: locale.resource(loc ? "PreferenceTooltipFilterLocale" : "PreferenceTooltipFilter", [ql.name, loc && locale.resource(loc)])
						}, filterContainer);

						_.each(widgets, function(w) {
							w.placeAt(filterContainer);
						});
					}
				});
			}
		},

		/**
		 * Render groups header and  view's groups info
		 */
		_renderGroups: function() {
			// output group information, but only if it is configurable in view; some views have fixed grouping which should not show up in custom view summary
			var groups = this.itemDetails.values.groupSettings

			if (groups && (groups.key1 || groups.key2) && this.view.canGroup()) {
				html.create("label", {
					innerText: locale.resource("PreferenceTooltipGroup")
				}, this.domNode);

				this._renderGroup(groups.key1);
				this._renderGroup(groups.key2);
			}
		},

		/**
		 * render single group header
		 * @param {C8AttributeKey} key 
		 */
		_renderGroup(key) {
			if (key) {
				var spec = this.view.getHeadingSpec(key);
				if (spec) {
					html.create("div", {
						innerText: spec.getExtendedName()
					}, this.domNode);
				}
			}
		},

		/**
		 * Render sort by header and view's sort by info
		 */
		_renderSortBy: function() {
			var sortList = [];
			if (!this.item.isDefaultView()) {
				var sorts = this.itemDetails.values.sortSettings;
				if (sorts && sorts.length) {
					_.each(sorts, function(obj) {
						var spec = this.view.getHeadingSpec(obj.key);
						if (spec) {
							sortList.push(" " + spec.getExtendedName());
						}
					}, this);
					if (sortList.length) {
						var sortByContainer = html.create("div", {"class": this.baseClass + "-sort-by-container"}, this.domNode);
						html.create("label", {
							innerText: locale.resource("PreferenceTooltipSort")
						}, sortByContainer);
						var sortStr = sortList.toString().trim();
						html.stringToDom(HTML_NAME, [sortStr], sortByContainer);
					}
				}
			}
		}
	});
});